import React, { FormEventHandler } from 'react';
import styled from 'styled-components';
import Link from '~/App/shared/components/Link';
import {
  BodyLink,
  Button,
  CheckIcon,
  Message,
  Spinner
} from '@kvdbil/components';
import { Session } from '~/App/shared/interfaces/store/Session';
import { useTranslation } from '~/Locale';
import { Form, FormItem } from './styled';
import {
  RegistrationRelatedLink,
  RegistrationSubtleSuccess
} from '~/App/shared/components/Auth/Registration/components/RegistrationElements';

const SubmitButton = styled(Button)`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const CheckMark = styled(CheckIcon)`
  margin-left: 0.25rem;
  position: relative;
`;

const SpinnerContainer = styled.div`
  margin-left: 0.25rem;
`;

export interface EmailSubmit {
  isVerified: boolean;
  isLoading: boolean;
  errors?: { status: number }[];
  handleSubmit: FormEventHandler<HTMLFormElement>;
  handleResendEmail: CallableFunction;
  resend: 'loading' | 'success' | 'error' | undefined;
}

interface Props {
  token?: string;
  submit: EmailSubmit;
  session: Session;
}

const EmailVerificationForm = ({ submit, token, session }: Props) => {
  const { t } = useTranslation();
  const linkText = t('Go to my account');
  const linkRoute = '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_SETTINGS';

  return (
    <Form onSubmit={submit?.handleSubmit} noValidate>
      <FormItem>
        {submit.errors && submit.errors.length > 0 ? (
          <Message textCenter fullWidth withIcon type="error">
            {submit?.errors?.map(error => {
              if (error.status === 400) {
                return t('Invalid email token');
              }

              return t('Something went wrong');
            })}
          </Message>
        ) : null}
      </FormItem>
      <FormItem>
        {submit?.isVerified ? (
          <Message textCenter withIcon fullWidth type="success">
            {t('Your email has been verified')}
          </Message>
        ) : null}
      </FormItem>
      <FormItem textAlign="center">
        {submit?.isVerified && session.isAuthenticated && (
          <Link to={linkRoute}>
            <BodyLink>{linkText}</BodyLink>
          </Link>
        )}

        {submit?.isVerified && !session.isAuthenticated && (
          <Link to="/ROUTES.MEMBER/ROUTES.MEMBER_SIGN_IN">{t('Sign in')}</Link>
        )}

        {!submit?.isVerified ? (
          <SubmitButton
            color="secondary"
            isLoading={submit.isLoading}
            isDisabled={!token}
            type="submit"
          >
            {t('Verify email')}
          </SubmitButton>
        ) : null}
      </FormItem>

      {submit.errors?.find(item => item.status === 400) ? (
        <FormItem textAlign="center">
          <RegistrationRelatedLink onClick={e => submit.handleResendEmail(e)}>
            {t('Resend email verification')}
          </RegistrationRelatedLink>
          {submit?.resend === 'success' ? (
            <RegistrationSubtleSuccess>
              <CheckMark />
            </RegistrationSubtleSuccess>
          ) : null}
          {submit?.resend === 'loading' ? (
            <SpinnerContainer>
              <Spinner size="smaller" color="neutral" speed="fast" />
            </SpinnerContainer>
          ) : null}
        </FormItem>
      ) : null}
    </Form>
  );
};

export default EmailVerificationForm;
